import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';

type Value = 'yes' | 'no' | 'unspecified';

interface SponsorshipPolicyControlProps {
  jobId: number;
  initialValue: Value;
}

export function SponsorshipPolicyControl({
  jobId,
  initialValue,
}: SponsorshipPolicyControlProps) {
  const [value, setValue] = useState<Value>(initialValue);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (next: Value) => api.updateSponsorshipPolicy(jobId, next),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to update');
    },
  });

  const onChange = (next: Value) => {
    setValue(next);
    mutation.mutate(next);
  };

  return (
    <div
      className="flex flex-col gap-1"
      data-testid="sponsorship-policy-control"
    >
      <label
        htmlFor={`sponsorship-${jobId}`}
        className="text-sm font-medium text-gray-700 dark:text-gray-300"
      >
        Visa sponsorship for this role
      </label>
      <select
        id={`sponsorship-${jobId}`}
        value={value}
        onChange={(e) => onChange(e.target.value as Value)}
        disabled={mutation.isPending}
        className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
      >
        <option value="yes">Yes — sponsorship offered</option>
        <option value="no">No — not offered</option>
        <option value="unspecified">Not specified — flag for review</option>
      </select>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Used to evaluate candidate screening answers about sponsorship.
        Override is preserved across re-extractions.
      </p>
      {error && (
        <p
          className="text-xs text-red-600 dark:text-red-400"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}
